import React, { Fragment, useEffect, useState, useCallback } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import moment from 'moment-timezone'
import { useEditSequenceContext } from './context/EditSequenceContext'
import EditSequenceLayout from './EditSequenceLayout/EditSequenceLayout'
import useGraphQLFetch from '../../../../../../../../Utils/Hooks/useGraphQLFetch'
import { findPipeline } from '../../../gql'
import { PATHS } from '../../../../../../../../Config/Routing/paths'
import LoadingIndicator from '../../../../../../../../Components/LoadingIndicator/LoadingIndicator'
import { isEqual } from 'lodash'
import ConfirmDialog from '../../../../../../../../Components/ConfirmDialog/ConfirmDialog'
import { useSequenceSubmit } from './hooks/useSequenceSubmit'
import {
  isScheduledTrigger,
  calculateTimeBefore,
  handleConfirmationDialog,
} from '../../../utils'

const formatTemplate = template => {
  const bodyParsed = JSON.parse(template.body)
  return { ...template, body: bodyParsed }
}

export const EditSequence = ({
  url,
  cityId,
  cityEmail,
  cityTimezone,
  isOnsiteTrainingEnabled,
}) => {
  const { sequenceId } = useParams()
  const history = useHistory()
  const {
    editSequenceContextState,
    updateSequence,
    setHasChanges,
    setInitialSequence,
    updateStage,
  } = useEditSequenceContext()
  const {
    onSubmit,
    submitErrors,
    submitLoading,
    savedSequenceId,
  } = useSequenceSubmit(
    history.location.state ? history.location.state.seqStatus : undefined,
    cityTimezone,
  )
  const {
    hasChanges,
    sequence,
    stage,
    errors: contextErrors,
    initialSequence,
  } = editSequenceContextState
  const [isLoading, setIsLoading] = useState(true)
  const [confirmDialogData, setConfirmDialogData] = useState({ isOpen: false })
  const [disableSubmit, setDisableSubmit] = useState(false)
  const [updateInitialSequence, setUpdateInitialSequence] = useState(true)

  useEffect(() => {
    if (
      initialSequence &&
      sequence &&
      (!isEqual(initialSequence, sequence) || stage !== initialSequence.stage)
    ) {
      setHasChanges(true)
    } else {
      setHasChanges(false)
    }
  }, [initialSequence, sequence, stage])

  const {
    data: sequenceData,
    isLoaded: isSequencesLoaded,
    refetch: refetchSequences,
  } = useGraphQLFetch({
    fetchFn: findPipeline,
    mapResponse: data => {
      if (!data.hasOwnProperty('findPipeline')) {
        return []
      }

      // Set time zone to DateSequenceTemplate start date
      const parsedData = JSON.parse(data.findPipeline)

      return parsedData
    },
    defaultFetchArgs: { id: sequenceId, addTemplates: true },
  })

  useEffect(() => {
    if (
      cityTimezone &&
      sequenceData &&
      (!sequence || updateInitialSequence) &&
      isSequencesLoaded
    ) {
      setUpdateInitialSequence(false)
      const dbSequence = sequenceData.find(s => s.id === sequenceId)
      if (
        dbSequence.DateSequenceTemplate &&
        dbSequence.DateSequenceTemplate.length
      ) {
        dbSequence.DateSequenceTemplate = dbSequence.DateSequenceTemplate.map(
          dateSequenceTemplate => {
            if (dateSequenceTemplate.start_date) {
              dateSequenceTemplate.start_date = moment
                .utc(Number(dateSequenceTemplate.start_date))
                .tz(cityTimezone)
                .format('YYYY-MM-DD HH:mm:ss')
            }
            return dateSequenceTemplate
          },
        )
      }

      const hasScheduledTrigger = dbSequence.Trigger.some(trigger =>
        isScheduledTrigger(trigger.triggerType.name),
      )

      dbSequence.Action.sort((a, b) => {
        if (a.time_delay == b.time_delay) {
          return a.id - b.id
        }
        return a.time_delay - b.time_delay
      })

      // if is a schedule trigger calculate time_before using trigger's target_value
      dbSequence.Action = dbSequence.Action.map(action => {
        if (hasScheduledTrigger) {
          return {
            ...action,
            time_before: String(
              calculateTimeBefore(
                dbSequence.Trigger[0].target_value,
                action.time_delay,
              ),
            ),
            time_delay: 0,
          }
        } else {
          return {
            ...action,
            time_before: '',
          }
        }
      })

      const actionsWithTemplateFormatted = dbSequence.Action.map(action => {
        if (
          action.actionType &&
          (action.actionType.name === 'EMAIL' ||
            action.actionType.name === 'SMS')
        ) {
          action.template = { ...formatTemplate(action.template) }
        }
        return action
      })

      const sequenceWithTemplates = {
        ...dbSequence,
        Action: [...actionsWithTemplateFormatted],
        hasScheduledTrigger,
      }

      updateStage(sequenceWithTemplates.stage)
      updateSequence({ ...sequenceWithTemplates })
      setInitialSequence({ ...sequenceWithTemplates })
      setIsLoading(false)
    }
  }, [
    cityTimezone,
    sequenceData,
    isSequencesLoaded,
    sequence,
    updateInitialSequence,
  ])

  const handleBackRedirect = () => {
    if (history.location.state && history.location.state.from == 'create') {
      history.push(url + PATHS.cities.ambassadorsEmails)
    } else {
      history.goBack()
    }
  }

  const handleBack = useCallback(() => {
    if (hasChanges) {
      handleConfirmationDialog({
        title: `There are unsaved changes. \n Do you want to leave without saving them?`,
        setConfirmDialogData,
        hasOptions: true,
        onConfirm: handleBackRedirect,
        rightButtonLable: 'LEAVE',
        leftButtonLable: 'STAY',
      })
    } else {
      handleBackRedirect()
    }
  }, [hasChanges])

  const handleSubmit = async () => {
    if (!submitLoading) {
      await onSubmit({
        sequence: sequence,
        cityId,
        stage: stage,
        url,
        initialSequence,
      })
      refetchSequences({ id: sequenceId, addTemplates: true })
      setUpdateInitialSequence(true)
    }
  }

  useEffect(() => {
    if (Array.isArray(submitErrors) && submitErrors.length) {
      alert(...submitErrors)
      if (savedSequenceId) {
        refetchSequences({ id: sequenceId, addTemplates: true })
        setUpdateInitialSequence(true)
      }
    }
  }, [submitErrors, savedSequenceId])

  useEffect(() => {
    if (Object.entries(contextErrors).length) {
      setDisableSubmit(true)
    } else {
      if (disableSubmit) setDisableSubmit(false)
    }
  }, [contextErrors])

  return isLoading || submitLoading ? (
    <LoadingIndicator style={{ height: '100vh' }} />
  ) : (
    <Fragment>
      <ConfirmDialog {...confirmDialogData} />
      <EditSequenceLayout
        cityId={cityId}
        cityEmail={cityEmail}
        isOnsiteTrainingEnabled={isOnsiteTrainingEnabled}
        handleBack={handleBack}
        onSubmit={handleSubmit}
        disableSubmit={disableSubmit}
        setConfirmDialogData={setConfirmDialogData}
        cityTimezone={cityTimezone}
      />
    </Fragment>
  )
}
